import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribing(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      toast.success("Successfully subscribed to our newsletter!");
      setEmail("");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info("You're already subscribed!");
      } else {
        toast.error("Failed to subscribe. Please try again.");
      }
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer data-testid="main-footer" className="bg-[#08090D] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 border border-amber-400/50 flex items-center justify-center">
                <span className="text-amber-400 font-bold text-xl">UV</span>
              </div>
              <div>
                <span className="text-white font-light tracking-widest text-sm uppercase block">
                  Unleashed Ventures
                </span>
                <span className="text-slate-500 text-xs tracking-wider">Est. 2019</span>
              </div>
            </div>
            <p className="text-slate-400 font-light leading-relaxed max-w-md">
              Strategic investors in micro-cap companies and acquirer of private entities. 
              We identify exceptional opportunities in healthcare, biotechnology, and emerging technologies.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-white text-xs uppercase tracking-widest mb-6">Navigation</h4>
            <nav className="space-y-3">
              <Link to="/" className="block text-slate-400 hover:text-amber-400 transition-colors text-sm">
                Home
              </Link>
              <Link to="/portfolio" className="block text-slate-400 hover:text-amber-400 transition-colors text-sm">
                Portfolio
              </Link>
              <Link to="/about" className="block text-slate-400 hover:text-amber-400 transition-colors text-sm">
                About
              </Link>
              <Link to="/news" className="block text-slate-400 hover:text-amber-400 transition-colors text-sm">
                News
              </Link>
              <Link to="/contact" className="block text-slate-400 hover:text-amber-400 transition-colors text-sm">
                Contact
              </Link>
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white text-xs uppercase tracking-widest mb-6">Connect</h4>
            <div className="space-y-3">
              <Link 
                to="/contact" 
                className="flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors text-sm"
              >
                Investor Inquiries
                <ArrowUpRight size={14} />
              </Link>
              <Link 
                to="/contact" 
                className="flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors text-sm"
              >
                Business Development
                <ArrowUpRight size={14} />
              </Link>
              <Link 
                to="/admin/login" 
                className="flex items-center gap-2 text-slate-500 hover:text-slate-400 transition-colors text-xs mt-8"
              >
                Admin Portal
              </Link>
            </div>
          </div>
        </div>

        {/* Newsletter */}
        <div className="mt-12 pt-12 border-t border-white/5">
          <div className="max-w-md">
            <h4 className="text-white text-xs uppercase tracking-widest mb-3">Stay Updated</h4>
            <p className="text-slate-500 text-sm mb-4">
              Subscribe to receive investment insights and company updates.
            </p>
            <form onSubmit={handleSubscribe} className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="flex-1 bg-white/5 border border-white/10 px-4 py-3 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-amber-400/50 transition-colors"
                data-testid="footer-newsletter-input"
              />
              <button
                type="submit"
                disabled={subscribing}
                className="bg-amber-400 hover:bg-amber-500 text-[#08090D] px-5 py-3 font-medium text-sm transition-colors disabled:opacity-50 flex items-center gap-2"
                data-testid="footer-newsletter-btn"
              >
                {subscribing ? (
                  <div className="w-4 h-4 border-2 border-[#08090D]/30 border-t-[#08090D] rounded-full animate-spin" />
                ) : (
                  <Send size={16} />
                )}
              </button>
            </form>
            <p className="text-slate-600 text-xs mt-3 leading-relaxed">
              By subscribing, you agree to receive marketing communications from Unleashed Ventures. 
              You may unsubscribe at any time. View our <Link to="/privacy" className="text-slate-500 hover:text-amber-400 underline">Privacy Policy</Link>.
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-xs">
            &copy; {currentYear} Unleashed Ventures. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-slate-500 hover:text-amber-400 transition-colors text-xs">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-slate-500 hover:text-amber-400 transition-colors text-xs">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
